import { Layout } from "@jambushrushti/components/layout/Layout";
import { useParams, Link } from "react-router-dom";
import { useEventsFromDB } from "@jambushrushti/hooks/useContent";
import { Card, CardContent } from "@jambushrushti/components/ui/card";
import { Button } from "@jambushrushti/components/ui/button";
import { Badge } from "@jambushrushti/components/ui/badge";
import {
  Calendar,
  MapPin,
  ArrowLeft,
  ExternalLink,
  Loader2,
} from "lucide-react";
import { format } from "date-fns";

const EventDetails = () => {
  const { id } = useParams<{ id: string }>();
  const { data: events = [], isLoading } = useEventsFromDB();

  const event = events.find(e => e.id === id);

  if (isLoading) {
    return (
      <Layout>
        <div className="flex min-h-screen items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (!event) {
    return (
      <Layout>
        <div className="flex min-h-screen flex-col items-center justify-center py-16">
          <Calendar className="mb-4 h-16 w-16 text-muted-foreground/30" />
          <h2 className="font-heading mb-2 text-2xl font-bold text-foreground">
            Event Not Found
          </h2>
          <p className="mb-6 text-muted-foreground">
            The event you're looking for doesn't exist.
          </p>
          <Button variant="hero" asChild>
            <Link to="/community/events">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Events
            </Link>
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Breadcrumb */}
      <section className="bg-secondary/30 py-4">
        <div className="container mx-auto px-4">
          <nav className="flex items-center gap-2 text-sm text-muted-foreground">
            <Link to="/" className="transition-colors hover:text-primary">
              Home
            </Link>
            <span>/</span>
            <Link
              to="/community/events"
              className="transition-colors hover:text-primary"
            >
              Events
            </Link>
            <span>/</span>
            <span className="text-foreground">{event.title}</span>
          </nav>
        </div>
      </section>

      {/* Event Details */}
      <section className="bg-background py-12 lg:py-16">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-4xl">
            <Button variant="ghost" asChild className="mb-6">
              <Link to="/community/events">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Events
              </Link>
            </Button>

            <Card variant="feature" className="overflow-hidden">
              {event.image_url && (
                <div className="h-64 w-full md:h-96">
                  <img
                    src={event.image_url}
                    alt={event.title}
                    className="h-full w-full object-cover"
                  />
                </div>
              )}
              <CardContent className="p-6 md:p-8">
                <Badge variant="default" className="mb-4">
                  Event
                </Badge>
                <h1 className="font-heading mb-4 text-2xl font-bold text-foreground md:text-3xl">
                  {event.title}
                </h1>

                <div className="mb-6 flex flex-wrap gap-6">
                  {event.event_date && (
                    <div className="flex items-center gap-2 text-foreground">
                      <Calendar className="h-5 w-5 text-primary" />
                      <span className="font-medium">
                        {format(new Date(event.event_date), "MMMM d, yyyy")}
                      </span>
                    </div>
                  )}
                  {event.location && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <MapPin className="h-5 w-5" />
                      <span>{event.location}</span>
                    </div>
                  )}
                </div>

                <div className="prose prose-lg max-w-none">
                  <p className="font-body text-base leading-relaxed text-muted-foreground">
                    {event.description}
                  </p>
                </div>

                <div className="mt-8 border-t border-border pt-6">
                  <Button variant="hero" asChild>
                    <a
                      href="https://siddhijambuparivar.com/events-list/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View on Official Website
                      <ExternalLink className="ml-2 h-4 w-4" />
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default EventDetails;
